import GHObjectType from './GHObjectType';
import GHObject from './GHObject';
import GHSliderObject from './GHSliderObject';
import GHValueListObject from './GHValueListObject';
import GHGeometryObject from './GHGeometryObject';
import GHInputParam from './GHInputParam';
import GHOutputParam from './GHOutputParam';
import GHToggleObject from './GHToggleParam';
// import GHInteractiveLengthObject from './GHInteractiveLengthObject'

import * as GHMath from '../math';

import OBase from './components/OBase';
import OInput from './components/OInput';
import OOutput from './components/OOutput';
import * as Components from './components';
import * as Geometry from './geometry';
import GenericObject from './geometry/GenericObject';
import GHMaterialListObject from './GHMaterialListObject';
import GHPaintListObject from './GHPaintListObject';

// https://developer.mozilla.org/ja/docs/Web/API/Document

export default class GHParser {
  constructor(dom, managers) {
    this.materialManager = managers.material;
    this.paintManager = managers.paint;

    this.sliders = [];
    this.toggles = [];
    this.valueLists = [];
    this.materialLists = [];

    this.geometries = [];
    this.components = [];
    this.meshes = [];
    this.handleMeshes = [];
    this.unit = 0.001;

    let definitions = this.filterChunks(Array.prototype.slice.call(dom.getElementsByTagName('chunks')), 'Definition');
    let definitionObjects = this.filterChunks(definitions, 'DefinitionObjects');
    let objects = this.filterChunks(definitionObjects, 'Object');

    objects.forEach((object) => {
      let items = object.getElementsByTagName('item');
      let instanceGuids = this.filterItems(items, 'InstanceGuid');
      let names = this.filterItems(items, 'Name');
      let nickNames = this.filterItems(items, 'NickName');

      let instanceGuid = instanceGuids[0];
      let name = names[0];
      let nickName = nickNames[0];

      let ghObject = new GHObject(name, instanceGuid, nickName);

      this.validate(items, name, nickName);

      switch (ghObject.objectType) {
        case GHObjectType.SLIDER:
          this.parseSlider(object, ghObject);
          break;
        case GHObjectType.TOGGLE:
          this.parseToggle(object, ghObject);
          break;
        case GHObjectType.NUMBER:
          this.parseNumber(object, items, ghObject);
          break;
        case GHObjectType.POINT:
          this.parsePoint(object, items, ghObject);
          break;
        case GHObjectType.VECTOR:
          this.parseVector(object, items, ghObject);
          break;
        case GHObjectType.BREP:
          this.parseBrep(object, items, ghObject);
          break;
        case GHObjectType.PANEL:
          this.parsePanel(object, items, ghObject);
          break;
        case GHObjectType.RELAY:
          this.parseRelay(object, items, ghObject);
          break;
        case GHObjectType.BUTTON:
          this.parseButton(object, ghObject);
          break;
        case GHObjectType.VALUELIST:
          this.parseValueList(object, ghObject);
          break;
        case GHObjectType.INTERACTIVEVISIBLE:
          this.parseInteractiveVisible(object, ghObject);
          break;
        case GHObjectType.EMATERIALLIST:
          this.parseEMaterialList(object, ghObject, this.materialManager.json);
          break;
        case GHObjectType.EPAINTLIST:
          this.parseEPaintList(object, ghObject, this.paintManager.paints);
          break;
        default:
          this.parseDefault(object, ghObject);
          break;
      }
    });
  }

  sequence() {
    this.sort();
    this.initGeometries();
    this.initMeshes();
    this.initHandles();
    this.visualizeMeshes();
  }

  // param_input, param_outputに直接オプションが指定されていればエラーにする
  //  - UseDegress should be false
  //  - Mapping option should be empty
  validate(items, name, nickName) {
    let mappings = this.filterItems(items, 'Mapping');
    if (mappings.length > 0) {
      throw new Error(`Path mapping option should be empty in ${name}.`);
    }

    let degrees = this.filterItems(items, 'UseDegrees').filter(value => (value === 'True') || (value === 'true'));
    if (degrees.length > 0) {
      throw new Error(`Angle option should be empty in ${name}.`);
    }
  }

  getQueryVariable() {
    let query = window.location.search.substring(1);
    let allVar = query.split("&");
    let splited = [];
    for (let variable of allVar) {
      let splitedVar = variable.split("=");
      splited.push(splitedVar);
    }
    return splited;
  }

  parseSlider(object, ghObject) {
    // handle url parameters(when first load the page) if exist
    let variables = this.getQueryVariable();
    let setValue = null;
    for (let variable of variables) {
      if (ghObject.instanceGuid == variable[0]) {
        setValue = Number(variable[1]);
      } else {
        continue;
      }
    }

    let sliderEl = this.filterByName(object.getElementsByTagName('chunk'), 'Slider')[0];
    let sliderItems = sliderEl.getElementsByTagName('item');
    let digits = this.filterItems(sliderItems, 'Digits')[0];
    let min = this.filterItems(sliderItems, 'Min')[0];
    let max = this.filterItems(sliderItems, 'Max')[0];
    let value = (setValue != null) ? setValue : this.filterItems(sliderItems, 'Value')[0];
    let interval = this.filterItems(sliderItems, 'Interval')[0];
    // console.log(sliderItems)

    let slider = new GHSliderObject(ghObject, digits, min, max, value, interval);

    const input = new GHInputParam('Number Slider', 'Number Slider', '', -1, '', value);
    slider.inputParams.push(input);

    const output = new GHOutputParam('Number Slider', 'Number Slider', slider.instanceGuid);
    slider.outputParams.push(output);

    this.geometries.push(slider);
    this.sliders.push(slider);
  }

  parseValueList(object, ghObject) {
    let valueList = new GHValueListObject(ghObject);

    let selectedVal = -1;

    let root = this.getElement(this.getElement(object, 'chunks'), 'chunk');
    let chunks = this.getElements(this.getElement(root, 'chunks'), 'chunk');
    chunks.forEach(chunk => {
      if (chunk.getAttribute('name') === 'ListItem') {
        let items = chunk.getElementsByTagName('item');
        let name = this.filterItems(items, 'Name')[0];
        let expression = this.filterItems(items, 'Expression')[0];
        let selectedS = this.filterItems(items, 'Selected')[0];

        valueList.keys.push(name);
        valueList.values.push(expression);
        if (selectedS === 'true') {
          valueList.selected = valueList.values.length - 1;
          selectedVal = expression;
        }
      }
    });

    const input = new GHInputParam('Value List', 'Value List', '', -1, '', selectedVal);
    valueList.inputParams.push(input);

    const output = new GHOutputParam('Value List', 'Value List', valueList.instanceGuid);
    valueList.outputParams.push(output);

    this.valueLists.push(valueList);
    this.geometries.push(valueList);
  }

  parseToggle(object, ghObject) {
    let toggle = new GHToggleObject(ghObject);

    let items = object.getElementsByTagName('item');
    let boolean = this.filterItems(items, 'ToggleValue')[0];
    if (boolean === 'false') {
      toggle.value = false;
    } else {
      toggle.value = true;
    }

    const input = new GHInputParam('Boolean Toggle', 'Boolean Toggle', '', -1, '', toggle.value);
    toggle.inputParams.push(input);

    const output = new GHOutputParam('Boolean Toggle', 'Boolean Toggle', toggle.instanceGuid);
    toggle.outputParams.push(output);

    this.toggles.push(toggle);
    this.geometries.push(toggle);
  }

  parseNumber(object, items, ghObject) {
    let sources = this.filterItems(items, 'Source');

    let sourceInput;
    if (sources.length > 0) {
      sourceInput = sources[0];
    }

    let defaultVal = 0.0;

    let geometryObject = new GHGeometryObject(ghObject, false);

    const input = new GHInputParam('Number', 'Num', sourceInput, -1, '', defaultVal);
    geometryObject.inputParams.push(input);

    const output = new GHOutputParam('Number', 'Num', geometryObject.instanceGuid);
    geometryObject.outputParams.push(output);

    this.geometries.push(geometryObject);
  }

  parsePoint(object, items, ghObject) {
    let sources = this.filterItems(items, 'Source');

    let sourceInput;
    if (sources.length > 0) {
      sourceInput = sources[0];
    }

    let geometryObject = new GHGeometryObject(ghObject, false);

    const input = new GHInputParam('Point', 'Pt', sourceInput, -1, '', '');
    geometryObject.inputParams.push(input);

    const output = new GHOutputParam('Point', 'Pt', geometryObject.instanceGuid);
    geometryObject.outputParams.push(output);

    this.geometries.push(geometryObject);
  }

  parseVector(object, items, ghObject) {
    let sources = this.filterItems(items, 'Source');

    let sourceInput;
    if (sources.length > 0) {
      sourceInput = sources[0];
    }

    let geometryObject = new GHGeometryObject(ghObject, false);

    const input = new GHInputParam('Vector', 'Vec', sourceInput, -1, '', '');
    geometryObject.inputParams.push(input);

    const output = new GHOutputParam('Vector', 'Vec', geometryObject.instanceGuid);
    geometryObject.outputParams.push(output);

    this.geometries.push(geometryObject);
  }

  parseBrep(object, items, ghObject) {
    let sources = this.filterItems(items, 'Source');

    let sourceInput;
    if (sources.length > 0) {
      sourceInput = sources[0];
    }

    let hiddens = this.filterByName(items, 'Hidden');
    let hidden = (hiddens.length > 0) ? hiddens[0] : false;

    let geometryObject = new GHGeometryObject(ghObject, hidden);

    const input = new GHInputParam('Brep', 'Brep', sourceInput, -1, '', '');
    geometryObject.inputParams.push(input);

    const output = new GHOutputParam('Brep', 'Brep', geometryObject.instanceGuid);
    geometryObject.outputParams.push(output);

    this.geometries.push(geometryObject);
  }

  parsePanel(object, items, ghObject) {
    let sourceInput = '';
    let sources = this.filterItems(items, 'Source');
    if (sources.length > 0) {
      sourceInput = sources[0];
    }

    let userText = '';
    let userTexts = this.filterItems(items, 'UserText');
    let len = userTexts.length;

    // 'UserText'が存在しない場合はエラーを出す
    if (len > 0) {
      userText = userTexts[0];
    } else {
      throw new Error('UserText is not exist.');
    }

    // Panelの中身がリストとして記述されていないときは、改行などを削除する
    const multiline = this.filterItems(items, "Multiline")[0];
    if (multiline == 'true') {
      userText = userText.replace(/\s+/g, '');
    }

    let geometryObject = new GHGeometryObject(ghObject, false);

    const input = new GHInputParam('Panel', 'Panel', sourceInput, -1, '', userText);
    geometryObject.inputParams.push(input);

    const output = new GHOutputParam('Panel', 'Panel', geometryObject.instanceGuid);
    geometryObject.outputParams.push(output);

    this.geometries.push(geometryObject);
  }

  parseRelay(object, items, ghObject) {
    let sources = this.filterItems(items, 'Source');

    let sourceInput;
    if (sources.length > 0) {
      sourceInput = sources[0];
    }

    let geometryObject = new GHGeometryObject(ghObject, true);
    const input = new GHInputParam('Relay', 'Relay', sourceInput, -1, '', '');
    geometryObject.inputParams.push(input);

    const output = new GHOutputParam('Relay', 'Relay', geometryObject.instanceGuid);
    geometryObject.outputParams.push(output);

    // console.log(geometryObject)

    this.geometries.push(geometryObject);
  }

  parseInteractiveVisible(object, ghObject) {
    this.parseDefault(object, ghObject, false);
  }

  parseEMaterialList(object, ghObject, materials) {
    const container = this.getElement(this.getElement(object, 'chunks'), 'chunk');
    const paramChunks = Array.from(this.getElement(container, 'chunks').children);
    const paramInput = paramChunks.find(chunk => chunk.getAttribute('name') === 'param_input');
    const paramOutput = paramChunks.find(chunk => chunk.getAttribute('name') === 'param_output');

    const sources = this.filterItems(this.getItems(paramInput), 'Source');
    const sourceInput = (sources.length > 0) ? sources[0] : null;

    const oGuids = this.filterItems(this.getItems(paramOutput), 'InstanceGuid');
    const oGuid = oGuids.length > 0 ? oGuids[0] : '';

    const mList = new GHMaterialListObject(ghObject, materials);

    const input = new GHInputParam('thickness', 'thickness', sourceInput, -1, '', 18);
    mList.inputParams.push(input);

    const output = new GHOutputParam('materialId', 'materialId', oGuid);
    mList.outputParams.push(output);

    this.materialLists.push(mList);
    this.geometries.push(mList);
  }

  parseEPaintList(object, ghObject, paints) {
    const container = this.getElement(this.getElement(object, 'chunks'), 'chunk');
    const paramChunks = Array.from(this.getElement(container, 'chunks').children);
    const paramOutput = paramChunks.find(chunk => chunk.getAttribute('name') === 'param_output');

    const oGuids = this.filterItems(this.getItems(paramOutput), 'InstanceGuid');
    const oGuid = oGuids.length > 0 ? oGuids[0] : '';

    const pList = new GHPaintListObject(ghObject, paints);

    const output = new GHOutputParam('paintId', 'paintId', oGuid);
    pList.outputParams.push(output);

    this.materialLists.push(pList);
    this.geometries.push(pList);
  }

  parseDefault(object, ghObject, useHidden = true) {
    // let chunks = this.getElements(this.getElement(object, 'chunks'), 'chunk')
    // let container = this.filterByName(chunks, 'Container')
    // console.log(chunks, container)

    let container = this.getElements(this.getElement(object, 'chunks'), 'chunk')[0];
    let items = this.getElements(this.getElement(container, 'items'), 'item');

    let hidden = false;
    if (useHidden) {
      let hiddens = this.filterByName(items, 'Hidden');
      hidden = (hiddens.length > 0) ? hiddens[0] : false;
    }

    let geometry = new GHGeometryObject(ghObject, hidden);

    let paramChunks = this.getElements(this.getElement(container, 'chunks'), 'chunk');
    let parameters = this.filterByName(paramChunks, 'ParameterData');

    if (parameters.length > 0) {
      let paramEls = this.getChunks(parameters[0]);
      for (let i in paramEls) {
        let paramEl = paramEls[i];
        let paramItems = this.getItems(paramEl);
        let name = paramEl.getAttribute('name');
        if (name === 'InputParam') {
          const input = this.parseInput(paramEl, paramItems);
          geometry.inputParams.push(input);
        } else if (name === 'OutputParam') {
          const output = this.parseOutput(paramItems);
          geometry.outputParams.push(output);
        }
      }
    } else {
      for (let i in paramChunks) {
        let paramChunk = paramChunks[i];
        let attrib = paramChunk.getAttribute('name');
        let attribs = attrib.split('_');
        if (attribs.length === 2) {
          if (attribs[0] === 'param') {
            let paramItems = this.getItems(paramChunk);
            if (attribs[1] === 'input') {
              const input = this.parseInput(paramChunk, paramItems);
              geometry.inputParams.push(input);
            } else if (attribs[1] === 'output') {
              const output = this.parseOutput(paramItems);
              geometry.outputParams.push(output);
            }
          }
        }
      }
    }

    this.geometries.push(geometry);
  }

  parseInput(paramEl, paramItems) {
    let paramSourceGuid = '';
    let paramAccess = -1;
    let paramTypeHintId = '';

    let sources = this.filterItems(paramItems, 'Source');
    if (sources.length > 0) {
      paramSourceGuid = sources[0];
    }

    let accesses = this.filterItems(paramItems, 'ScriptParamAccess');
    if (accesses.length > 0) {
      paramAccess = accesses[0];
    }

    let typeHints = this.filterItems(paramItems, 'TypeHintID');
    if (typeHints.length > 0) {
      paramTypeHintId = typeHints[0];
    }

    let persistentData = this.filterByName(this.getChunks(paramEl), 'PersistentData');
    let paramDefaultValue = this.parseDefaultValue(persistentData);

    let paramName = this.filterByName(paramItems, 'Name')[0].textContent;
    let paramNickName = this.filterByName(paramItems, 'NickName')[0].textContent;
    return new GHInputParam(paramName, paramNickName, paramSourceGuid, paramAccess, paramTypeHintId, paramDefaultValue);
  }

  parseOutput(paramItems) {
    let paramInstanceGuid = this.filterByName(paramItems, 'InstanceGuid')[0].textContent;
    let paramName = this.filterByName(paramItems, 'Name')[0].textContent;
    let paramNickName = this.filterByName(paramItems, 'NickName')[0].textContent;
    return new GHOutputParam(paramName, paramNickName, paramInstanceGuid);
  }

  parseDefaultValue(persistentData) {
    if (persistentData.length > 0) {
      let data = persistentData[0];
      let chunk = this.getChunks(data)[0];
      let chunk2 = this.getChunks(chunk)[0];
      let items = this.getItems(chunk2);
      for (let i = 0, n = items.length; i < n; i++) {
        let item = items[i];
        const attrib = item.getAttribute('name');
        switch (attrib) {
          case 'number':
            return Number(item.textContent);
          case 'Coordinate':
            let cx = this.getElement(item, 'X').textContent;
            let cy = this.getElement(item, 'Y').textContent;
            let cz = this.getElement(item, 'Z').textContent;
            return new THREE.Vector3(Number(cx), Number(cy), Number(cz));
          case 'vector':
            let vx = this.getElement(item, 'X').textContent;
            let vy = this.getElement(item, 'Y').textContent;
            let vz = this.getElement(item, 'Z').textContent;
            return new THREE.Vector3(Number(vx), Number(vy), Number(vz));
          case 'Interval':
          case 'interval':
            let a = this.getElement(item, 'A').textContent;
            let b = this.getElement(item, 'B').textContent;
            return new Geometry.Interval(a, b);
          case 'boolean':
            return (item.textContent === 'true');
          case 'plane':
            let ox = this.getElement(item, 'Ox').textContent;
            let oy = this.getElement(item, 'Oy').textContent;
            let oz = this.getElement(item, 'Oz').textContent;

            let xx = this.getElement(item, 'Xx').textContent;
            let xy = this.getElement(item, 'Xy').textContent;
            let xz = this.getElement(item, 'Xz').textContent;

            let yx = this.getElement(item, 'Yx').textContent;
            let yy = this.getElement(item, 'Yy').textContent;
            let yz = this.getElement(item, 'Yz').textContent;

            let origin = new THREE.Vector3(Number(ox), Number(oy), Number(oz));
            let xAxis = new THREE.Vector3(Number(xx), Number(xy), Number(xz));
            let yAxis = new THREE.Vector3(Number(yx), Number(yy), Number(yz));
            return GHMath.constructPlane(origin, xAxis, yAxis, 'Parse default plane');
          case 'path':
            return item.textContent;
          case 'TypeName':
            break;
          default:
            // console.warn('attribute not found', attrib)
            break;
        }
      }
    }

    return null;
  }

  sort() {
    let tempCheckList = [];
    this.geometries.forEach((g) => {
      tempCheckList.push(g);
    });

    let tempOutList = [];
    while (tempCheckList.length > 0) {
      for (let i = tempCheckList.length - 1; i >= 0; i--) {
        let ghObj = tempCheckList[i];
        let inputs = ghObj.inputParams;

        let found = false;
        for (let j = 0, n = inputs.length; j < n; j++) {
          let input = inputs[j];
          let source = input.sourceGuid;
          let results = this.geometries.filter((geo) => {
            return geo.outputParams.find((output) => output.instanceGuid === source) !== undefined;
          });
          if (results.length > 0) {
            let tempOut = tempOutList.find((temp) => temp.instanceGuid === results[0].instanceGuid);
            if (tempOut === undefined) {
              found = true;
              break;
            }
          }
        }

        if (!found) {
          tempOutList.push(ghObj);
          tempCheckList.splice(i, 1);
        }
      }
    }

    this.geometries = tempOutList;
  }

  initGeometries() {
    for (let i = 0, n = this.geometries.length; i < n; i++) {
      let ghGeom = this.geometries[i];
      this.convertGeometryToOBase(ghGeom, this.geometries, this.components);
    }

    this.updateGeometries();
  }

  debugComponent(component) {
    console.log(component);
    console.log(Object.keys(component.inputs).map(key => {
      return `${key} ${component.inputs[key].ghInputParam.sourceGuid}`;
    }));
    console.log(Object.keys(component.outputs).map(key => {
      return `${key} ${component.outputs[key].ghOutputParam.instanceGuid}`;
    }));
  }

  updateGeometries() {
    this.components.forEach((component) => {
      // check connections between components
      // this.debugComponent(component)

      try {
        component.update();
      } catch (e) {
        console.error(e);
        let name = component.constructor.name;
        let message = e.toString();
        console.log(`error in ${component.ghGeometryObject.instanceGuid}`);
        throw new Error(`${name} error: ${message}`);
      }
    });
  }

  updateHandleComponents() {
    this.handleMeshes.forEach((handleMesh) => {
      handleMesh.component.updateHandlePosRatio(handleMesh.component.handlePosRatio);
    });

    this.handleMeshes.forEach((handleMesh) => {
      handleMesh.update(handleMesh.component.handlePos);
    });
  }

  initHandles(scene = null) {
    if (scene !== null) {
      for (let n = 0; n < this.handleMeshes.length; n++) {
        let mesh = this.handleMeshes[n];
        scene.remove(mesh);
        mesh.geometry.dispose();
        mesh.material.dispose();
        mesh = undefined;
      }
      this.handleMeshes = [];
    }

    this.components.forEach((component) => {
      if (component.ghGeometryObject.typeName === 'InteractiveLength') {
        let pos = component.handlePos.clone();
        let handleMesh = new Geometry.GHHandle(pos, component, this.unit);
        this.handleMeshes.push(handleMesh);
      }
    });
  }

  initMeshes(scene = null) {
    if (scene !== null) {
      for (let n = 0; n < this.meshes.length; n++) {
        let mesh = this.meshes[n];
        scene.remove(mesh);
        mesh.geometry.dispose();
        mesh.material.dispose();
        mesh = undefined;
      }
      this.meshes = [];
    }

    this.components.forEach((component) => {
      if (!component.ghGeometryObject.hidden) {
        let values = Object.values(component.outputs);
        values.forEach((output) => {
          if (output.paramObject.value !== null && output.paramObject.value !== undefined) {
            let dataTree = output.paramObject.value;

            let hasMesh = false;

            for (let i = 0, n = dataTree.branches.length; i < n; i++) {
              let branch = dataTree.branches[i];
              for (let j = 0, m = branch.length; j < m; j++) {
                let val = branch[j];
                if (val instanceof THREE.Geometry) {
                  hasMesh = true;
                  break;
                }
              }
            }

            if (hasMesh) {
              for (let i = 0; i < dataTree.branches.length; i++) {
                for (let n = 0; n < dataTree.branches[i].length; n++) {
                  let genobj = new GenericObject();
                  let dt = new Geometry.DataTree();
                  dt.add(dataTree.branches[i][n], new Geometry.GHPath(0));
                  genobj.value = dt;
                  let mesh = new Geometry.GHMesh(genobj);
                  mesh.update({ material: this.materialManager, paint: this.paintManager });
                  this.meshes.push(mesh);
                  // console.log(mesh)
                }
              }
            }
          }
        });
      }
    });
  }

  visualizeMeshes() {
    this.meshes.forEach(mesh => {
      mesh.update({ material: this.materialManager, paint: this.paintManager });
    });
    return this.meshes;
  }

  convertGeometryToOBase(ghGeom, ghGeometries, components) {
    let obase = new OBase();
    obase.ghGeometryObject = ghGeom;
    for (let i in ghGeom.inputParams) {
      let ghInput = ghGeom.inputParams[i];
      let oInput = new OInput(ghInput);

      let outResult = this.findOutput(components, oInput);
      if (outResult !== undefined) {
        oInput.paramObject = outResult.paramObject;
        oInput.paramType = outResult.paramType;
      }

      /*
      if (oInput.paramObject !== null) {
        console.log(ghInput.name, oInput.paramObject)
      }
      */
      obase.inputs[ghInput.name] = oInput;
    }

    for (let i in ghGeom.outputParams) {
      let ghOutput = ghGeom.outputParams[i];
      let oOutput = new OOutput(ghOutput);
      oOutput.paramObject = new Geometry.GenericObject();
      obase.outputs[ghOutput.name] = oOutput;
    }

    this.setupComponent(obase, components, ghGeom);
  }

  findOutput(components, oInput) {
    for (let i in components) {
      let comp = components[i];
      let values = Object.values(comp.outputs);
      let found = values.find((value) => {
        return value.ghOutputParam.instanceGuid === oInput.ghInputParam.sourceGuid;
      });
      if (found !== undefined) {
        return found;
      }
    }

    return undefined;
  }

  setupComponent(obase, components, ghGeom) {
    let className = ghGeom.typeName.replace(new RegExp(' ', 'g'), '');
    // console.log(className)

    let constructor = Components[className];
    if (constructor === undefined) {
      let msg = className + ' component not found';
      throw new Error(msg);
    }
    // console.log(constructor.name)

    let component = new constructor(obase);
    // console.log(className, component)
    components.push(component);
  }

  getElement(root, nodeName) {
    for (let i = 0, n = root.childNodes.length; i < n; i++) {
      let child = root.childNodes[i];
      if (nodeName === child.nodeName) {
        return child;
      }
    }

    return null;
  }

  getElements(root, nodeName) {
    let elements = [];

    for (let i = 0, n = root.childNodes.length; i < n; i++) {
      let child = root.childNodes[i];
      if (nodeName === child.nodeName) {
        elements.push(child);
      }
    }

    return elements;
  }

  getChunks(root) {
    return this.getElements(this.getElement(root, 'chunks'), 'chunk');
  }

  getItems(root) {
    return this.getElements(this.getElement(root, 'items'), 'item');
  }

  filterByName(els, name) {
    let result = [];
    for (let i in els) {
      let el = els[i];
      if (typeof (el) !== 'object') continue;

      if (el.getAttribute('name') === name) {
        result.push(el);
      }
    }
    return result;
  }

  filterChunks(chunks, name) {
    let result = [];

    for (let i in chunks) {
      let chunk = chunks[i];
      if (typeof (chunk) !== 'object') continue;
      let els = chunk.getElementsByTagName('chunk');
      for (let j in els) {
        let el = els[j];
        if (typeof (el) !== 'object') continue;

        if (el.getAttribute('name') === name) {
          result.push(el);
        }
      }
    }

    return result;
  }

  filterItems(items, name) {
    let result = this.filterByName(items, name);
    return result.map(item => { return item.textContent; });
  }
}



// WEBPACK FOOTER //
// ./src/assets/javascripts/gh/GHParser.js